import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DONE_STEP_ID,
  fetchCareerUpApplicationProgress,
  upsertCareerUpApplicationProgress,
} from './careerUpApplicationApi';
import type {
  CareerUpApplicationFormValues,
  CareerUpApplicationStep,
  YesNo,
} from './careerUpApplicationTypes';

export type CareerUpApplicationCommittedEntry = {
  stepIndex: number;
  stepId: CareerUpApplicationStep;
  displayText: string;
  rawValue: string;
};

type StepDef = {
  stepId: CareerUpApplicationStep;
  fieldName: keyof CareerUpApplicationFormValues;
  kind: 'choice' | 'read' | 'agree';
};

const STEP_ORDER: CareerUpApplicationStep[] = ['confirmNoCard', 'readConsentDocument', 'agreeApply'];
const NEXT_ASSISTANT_QUESTION_DELAY_MS = 520;

function stepOrdinal(stepId: CareerUpApplicationStep): number {
  return STEP_ORDER.indexOf(stepId);
}

function isYes(raw: string): boolean {
  return raw.trim().toLowerCase() === 'yes' || raw.trim().toLowerCase() === 'true';
}

function formatYesNo(t: (k: string) => string, raw: string): string {
  return isYes(raw) ? t('submitDocuments.careerUpApplication.yes') : t('submitDocuments.careerUpApplication.no');
}

function formatReadConfirmed(t: (k: string) => string): string {
  return t('submitDocuments.careerUpApplication.readConfirmed');
}

function formatAgree(t: (k: string) => string): string {
  return t('submitDocuments.careerUpApplication.agreeCheckbox');
}

function buildSteps(values: CareerUpApplicationFormValues): StepDef[] {
  const noCard = values.confirmNoCard;
  if (noCard === 'no') {
    return [{ stepId: 'confirmNoCard', fieldName: 'confirmNoCard', kind: 'choice' }];
  }
  return [
    { stepId: 'confirmNoCard', fieldName: 'confirmNoCard', kind: 'choice' },
    { stepId: 'readConsentDocument', fieldName: 'consentDocumentRead', kind: 'read' },
    { stepId: 'agreeApply', fieldName: 'agreeApply', kind: 'agree' },
  ];
}

function cursorStepIdForSave(steps: StepDef[], stepIndex: number): string | null {
  if (stepIndex >= steps.length) return DONE_STEP_ID;
  return steps[stepIndex]?.stepId ?? null;
}

function upsertCommittedEntry(
  committed: CareerUpApplicationCommittedEntry[],
  entry: CareerUpApplicationCommittedEntry
): CareerUpApplicationCommittedEntry[] {
  const next = [...committed];
  const idx = next.findIndex((c) => c.stepId === entry.stepId);
  if (idx >= 0) {
    next[idx] = entry;
    return next;
  }
  next.push(entry);
  return next;
}

function trimCommittedThrough(
  committed: CareerUpApplicationCommittedEntry[],
  throughStepId: CareerUpApplicationStep
): CareerUpApplicationCommittedEntry[] {
  const max = stepOrdinal(throughStepId);
  return committed.filter((c) => stepOrdinal(c.stepId) <= max);
}

function clearDownstreamFields(
  values: CareerUpApplicationFormValues,
  fromStepId: CareerUpApplicationStep
): CareerUpApplicationFormValues {
  const next = { ...values };
  if (stepOrdinal(fromStepId) < stepOrdinal('readConsentDocument')) {
    delete next.consentDocumentRead;
  }
  if (stepOrdinal(fromStepId) < stepOrdinal('agreeApply')) {
    delete next.agreeApply;
  }
  return next;
}

function displayTextForStep(
  t: (k: string) => string,
  step: StepDef,
  raw: string
): string {
  if (step.kind === 'agree') return formatAgree(t);
  if (step.kind === 'read') return formatReadConfirmed(t);
  return formatYesNo(t, raw);
}

function rebuildFromProgress(
  values: CareerUpApplicationFormValues,
  currentStepId: string | null | undefined,
  t: (k: string) => string
): {
  stepIndex: number;
  committed: CareerUpApplicationCommittedEntry[];
  hasCardConflict: boolean;
} {
  if (values.confirmNoCard === 'no') {
    return {
      stepIndex: 1,
      committed: [
        {
          stepIndex: 0,
          stepId: 'confirmNoCard',
          rawValue: 'no',
          displayText: formatYesNo(t, 'no'),
        },
      ],
      hasCardConflict: true,
    };
  }

  if (currentStepId === DONE_STEP_ID) {
    const steps = buildSteps(values);
    const committed: CareerUpApplicationCommittedEntry[] = steps.map((s, i) => {
      const raw = String(values[s.fieldName] ?? '').trim();
      return {
        stepIndex: i,
        stepId: s.stepId,
        rawValue: raw,
        displayText: displayTextForStep(t, s, raw),
      };
    });
    return {
      stepIndex: steps.length,
      committed,
      hasCardConflict: false,
    };
  }

  const steps = buildSteps(values);
  const idx = currentStepId ? steps.findIndex((s) => s.stepId === currentStepId) : -1;
  const stepIndex = idx >= 0 ? idx : 0;
  const committed: CareerUpApplicationCommittedEntry[] = [];
  for (let i = 0; i < stepIndex; i++) {
    const s = steps[i];
    const raw = String(values[s.fieldName] ?? '').trim();
    committed.push({
      stepIndex: i,
      stepId: s.stepId,
      rawValue: raw,
      displayText: displayTextForStep(t, s, raw),
    });
  }
  return {
    stepIndex,
    committed,
    hasCardConflict: false,
  };
}

export function useCareerUpApplicationFlow(
  currentEmail: string | null | undefined,
  invitationId: string | null | undefined,
  t: (key: string) => string
) {
  const [isLoading, setIsLoading] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [values, setValues] = useState<CareerUpApplicationFormValues>({});
  const [stepIndex, setStepIndex] = useState(0);
  const [committed, setCommitted] = useState<CareerUpApplicationCommittedEntry[]>([]);
  const [hasCardConflict, setHasCardConflict] = useState(false);
  const [assistantQuestionVisible, setAssistantQuestionVisible] = useState(true);
  const [editingCommitIndex, setEditingCommitIndex] = useState<number | null>(null);
  const [agreeChecked, setAgreeChecked] = useState(false);
  const threadEndRef = useRef<HTMLDivElement | null>(null);

  const steps = useMemo(() => buildSteps(values), [values]);

  const currentStep = useMemo((): StepDef | null => {
    if (!steps.length) return null;
    if (editingCommitIndex !== null) return steps[editingCommitIndex] ?? null;
    if (stepIndex < steps.length) return steps[stepIndex];
    return null;
  }, [steps, stepIndex, editingCommitIndex]);

  const isComplete =
    !hasCardConflict && steps.length > 0 && stepIndex >= steps.length && editingCommitIndex === null;

  const showSummaryBlock = committed.some(
    (c) => c.stepId === 'readConsentDocument' && isYes(c.rawValue)
  );

  useEffect(() => {
    let cancelled = false;
    const email = currentEmail?.trim();
    const inv = invitationId?.trim();
    if (!email || !inv) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    void fetchCareerUpApplicationProgress(email, inv).then((payload) => {
      if (cancelled) return;
      const v = payload?.values ?? {};
      setValues(v);
      const rebuilt = rebuildFromProgress(v, payload?.currentStepId, t);
      setStepIndex(rebuilt.stepIndex);
      setCommitted(rebuilt.committed);
      setHasCardConflict(rebuilt.hasCardConflict);
      setAgreeChecked(v.agreeApply === 'true');
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [currentEmail, invitationId, t]);

  const persist = useCallback(
    async (nextValues: CareerUpApplicationFormValues, nextStepIndex: number, stepsSnapshot?: StepDef[]) => {
      const email = currentEmail?.trim();
      const inv = invitationId?.trim();
      if (!email || !inv) return null;
      const nextSteps = stepsSnapshot ?? buildSteps(nextValues);
      const cursor = cursorStepIdForSave(nextSteps, nextStepIndex);
      setSaveError(null);
      const saved = await upsertCareerUpApplicationProgress({
        email,
        invitationId: inv,
        values: nextValues,
        currentStepId: cursor,
      });
      if (!saved) {
        setSaveError(t('submitDocuments.careerUpApplication.saveFailed'));
        return null;
      }
      return saved;
    },
    [currentEmail, invitationId, t]
  );

  const revealNextQuestion = useCallback(() => {
    setAssistantQuestionVisible(false);
    window.setTimeout(() => setAssistantQuestionVisible(true), NEXT_ASSISTANT_QUESTION_DELAY_MS);
  }, []);

  const finishEditStep = useCallback(
    async (
      entry: CareerUpApplicationCommittedEntry,
      nextValues: CareerUpApplicationFormValues,
      editedStepId: CareerUpApplicationStep
    ) => {
      const cleared = clearDownstreamFields(nextValues, editedStepId);
      const nextCommitted = trimCommittedThrough(upsertCommittedEntry(committed, entry), editedStepId);
      const nextSteps = buildSteps(cleared);
      const editedOrdinal = stepOrdinal(editedStepId);

      let nextIdx = editedOrdinal + 1;
      while (nextIdx < nextSteps.length) {
        const s = nextSteps[nextIdx];
        const raw = String(cleared[s.fieldName] ?? '').trim();
        if (s.kind === 'agree' || s.kind === 'read') {
          if (raw !== 'true') break;
        } else if (!raw || (raw !== 'yes' && raw !== 'no')) {
          break;
        }
        nextIdx += 1;
      }

      if (editedStepId === 'agreeApply' || !cleared.agreeApply) {
        setAgreeChecked(cleared.agreeApply === 'true');
      }
      if (editedOrdinal < stepOrdinal('agreeApply')) {
        setAgreeChecked(false);
      }

      setValues(cleared);
      setCommitted(nextCommitted);
      setEditingCommitIndex(null);
      setHasCardConflict(false);
      setStepIndex(nextIdx);
      await persist(cleared, nextIdx, nextSteps);
      revealNextQuestion();
    },
    [committed, persist, revealNextQuestion]
  );

  const advanceConfirmNoCard = useCallback(
    async (choice: YesNo) => {
      if (!choice) return;
      const entry: CareerUpApplicationCommittedEntry = {
        stepIndex: editingCommitIndex ?? stepIndex,
        stepId: 'confirmNoCard',
        rawValue: choice,
        displayText: formatYesNo(t, choice),
      };
      const nextValues = { ...values, confirmNoCard: choice };

      if (choice === 'no') {
        if (editingCommitIndex !== null) setEditingCommitIndex(null);
        const saved = await persist(nextValues, 1);
        if (!saved) return;
        setValues(nextValues);
        setCommitted([entry]);
        setHasCardConflict(true);
        setAgreeChecked(false);
        setStepIndex(1);
        return;
      }

      if (editingCommitIndex !== null) {
        await finishEditStep(entry, nextValues, 'confirmNoCard');
        return;
      }

      const saved = await persist(nextValues, 1);
      if (!saved) return;
      setValues(nextValues);
      setCommitted([entry]);
      setStepIndex(1);
      revealNextQuestion();
    },
    [values, persist, t, revealNextQuestion, editingCommitIndex, stepIndex, finishEditStep]
  );

  const advanceReadConsent = useCallback(async () => {
    const entry: CareerUpApplicationCommittedEntry = {
      stepIndex: editingCommitIndex ?? stepIndex,
      stepId: 'readConsentDocument',
      rawValue: 'true',
      displayText: formatReadConfirmed(t),
    };
    const nextValues = { ...values, consentDocumentRead: 'true' };

    if (editingCommitIndex !== null) {
      await finishEditStep(entry, nextValues, 'readConsentDocument');
      return;
    }

    const saved = await persist(nextValues, stepIndex + 1);
    if (!saved) return;
    setValues(nextValues);
    setCommitted((prev) => upsertCommittedEntry(prev, entry));
    setStepIndex((i) => i + 1);
    revealNextQuestion();
  }, [values, persist, stepIndex, t, revealNextQuestion, editingCommitIndex, finishEditStep]);

  const advanceAgree = useCallback(async () => {
    if (!agreeChecked) return;
    const entry: CareerUpApplicationCommittedEntry = {
      stepIndex: editingCommitIndex ?? stepIndex,
      stepId: 'agreeApply',
      rawValue: 'true',
      displayText: formatAgree(t),
    };
    const nextValues = { ...values, agreeApply: 'true' };

    if (editingCommitIndex !== null) {
      const nextCommitted = trimCommittedThrough(upsertCommittedEntry(committed, entry), 'agreeApply');
      const nextSteps = buildSteps(nextValues);
      setValues(nextValues);
      setCommitted(nextCommitted);
      setEditingCommitIndex(null);
      setStepIndex(nextSteps.length);
      await persist(nextValues, nextSteps.length, nextSteps);
      revealNextQuestion();
      return;
    }

    const saved = await persist(nextValues, steps.length);
    if (!saved) return;
    setValues(nextValues);
    setCommitted((prev) => upsertCommittedEntry(prev, entry));
    setStepIndex(steps.length);
    revealNextQuestion();
  }, [agreeChecked, values, persist, steps.length, t, revealNextQuestion, editingCommitIndex, stepIndex, committed]);

  const handleFinalComplete = useCallback(async () => {
    const email = currentEmail?.trim();
    const inv = invitationId?.trim();
    if (!email || !inv) return false;
    const saved = await upsertCareerUpApplicationProgress({
      email,
      invitationId: inv,
      values,
      currentStepId: DONE_STEP_ID,
    });
    if (!saved) {
      setSaveError(t('submitDocuments.careerUpApplication.saveFailed'));
      return false;
    }
    return true;
  }, [currentEmail, invitationId, values, t]);

  const jumpToEditCommitIndex = useCallback(
    (commitIndex: number) => {
      if (!isComplete || commitIndex < 0 || commitIndex >= committed.length) return;
      const entry = committed[commitIndex];
      const stepIdx = steps.findIndex((s) => s.stepId === entry.stepId);
      if (stepIdx < 0) return;
      if (entry.stepId === 'agreeApply') {
        setAgreeChecked(entry.rawValue === 'true');
      }
      setEditingCommitIndex(stepIdx);
      setAssistantQuestionVisible(true);
    },
    [isComplete, committed, steps]
  );

  const undo = useCallback(() => {
    if (committed.length === 0) return;
    const last = committed[committed.length - 1];
    const next = committed.slice(0, -1);
    const nextStepIndex = next.length;

    setCommitted(next);
    setStepIndex(nextStepIndex);
    setEditingCommitIndex(null);
    setHasCardConflict(false);

    const nextValues: CareerUpApplicationFormValues = { ...values };
    if (last.stepId === 'confirmNoCard') {
      delete nextValues.confirmNoCard;
      delete nextValues.consentDocumentRead;
      delete nextValues.agreeApply;
      setAgreeChecked(false);
    } else if (last.stepId === 'readConsentDocument') {
      delete nextValues.consentDocumentRead;
      delete nextValues.agreeApply;
      setAgreeChecked(false);
    } else if (last.stepId === 'agreeApply') {
      delete nextValues.agreeApply;
      setAgreeChecked(false);
    }
    setValues(nextValues);
    void persist(nextValues, nextStepIndex);
    setAssistantQuestionVisible(true);
  }, [committed, values, persist]);

  const showEditForCommitIndex = useCallback(
    (commitIndex: number) => {
      if (editingCommitIndex !== null) return false;
      if (hasCardConflict) return false;
      if (!isComplete || committed.length === 0) return false;
      return commitIndex >= 0 && commitIndex < committed.length;
    },
    [editingCommitIndex, hasCardConflict, isComplete, committed.length]
  );

  const showUndoForCommitIndex = useCallback(
    (commitIndex: number) => {
      if (editingCommitIndex !== null) return false;
      if (hasCardConflict) return commitIndex === committed.length - 1;
      if (isComplete && committed.length > 0) return commitIndex === committed.length - 1;
      return stepIndex === commitIndex + 1;
    },
    [editingCommitIndex, hasCardConflict, isComplete, committed.length, stepIndex]
  );

  return {
    t,
    isLoading,
    saveError,
    values,
    committed,
    isComplete,
    hasCardConflict,
    currentStep,
    assistantQuestionVisible,
    agreeChecked,
    setAgreeChecked,
    showSummaryBlock,
    advanceConfirmNoCard,
    advanceReadConsent,
    advanceAgree,
    handleFinalComplete,
    jumpToEditCommitIndex,
    undo,
    showEditForCommitIndex,
    showUndoForCommitIndex,
    editingCommitIndex,
    threadEndRef,
  };
}
